import { DialogTrigger, Input } from "@light-technology/components";
import { type ReactNode, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { api } from "../../state/api";
import { type AppResponse } from "../../state/api.types";
import { FormModal } from "../shared/FormModal";

type CreateWebhookForm = {
  url: string;
  secret: string;
};

type CreateWebhookModalProps = {
  app: AppResponse;
  children: ReactNode;
};

export const CreateWebhookModal = (props: CreateWebhookModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [createWebhook, createWebhookResult] = api.useCreateWebhookMutation();
  const { handleSubmit, control, reset } = useForm<CreateWebhookForm>({
    defaultValues: {
      url: "",
      secret: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = handleSubmit(({ url, secret }) => {
    createWebhook({ appUUID: props.app.uuid, url, secret });
    setIsOpen(false);
  });

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {props.children}
      <FormModal
        textTitle="Where would you like to receive the new webhook?"
        textCancel="Cancel"
        textSubmit="Create webhook"
        isLoading={createWebhookResult.isLoading}
        onCancel={() => {
          setIsOpen(false);
        }}
        onSubmit={onSubmit}
        data-ph-capture-attribute-action="submit-create-webhook"
      >
        <Controller
          control={control}
          name="url"
          rules={{ required: "URL is required" }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="url"
              label="URL"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="secret"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              className="mt-2xl"
              type="text"
              label="Secret"
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
      </FormModal>
    </DialogTrigger>
  );
};
